/* eslint-disable max-lines-per-function */
import { Ref, useRef } from 'react';
import { isEmpty } from 'lodash';

import { Visit, VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';
import { Address } from '@dreryk/address-common-api';

import {
   Button,
   ButtonVariant,
   FormValidationSnackbar,
   Panel,
   Scroll,
   ScrollHandle,
   StyledFlex,
} from '@dreryk/react-components';

import { PrescriptionPackage, usePrescriptionPackages } from '~libs.shared.prescription';

import { PatientId, VisitId } from '../PrescriptionIssuingPanel.types';
import { draftPrescriptionsTranslationKeys } from '../../../translations';
import { documentationScopeVisitor } from '../../../visit/VisitPanel.utils';

import { PrescriptionsBanner } from './banner/PrescriptionsBanner';
import { DraftPrescriptionsForm } from './draftPrescriptions/DraftPrescriptionsForm';
import {
   DraftPrescriptionsListUpdate,
   DraftPrescriptionsListUpdateApi,
} from './draftPrescriptions/DraftPrescriptionsListUpdate';
import { DraftPrescriptionsRouteLeavingGuard } from './draftPrescriptions/DraftPrescriptionsRouteLeavingGuard';
import { PrescriptionVisitDataRequiredActionInterceptor } from './draftPrescriptions/PrescriptionVisitDataRequiredActionInterceptor';
import { SendPrescriptionAction } from './draftPrescriptions/SendPrescriptionAction';
import { PrescriptionPackagePrintLink } from './prescription/print/PrescriptionPackagePrintLink';
import { OnPrescriptionsSent, PrescriptionsApi } from './prescriptions.types';
import { PrescriptionsAdvertisementBox, PrescriptionsAdvertisementBoxPortal } from './PrescriptionsAdvertisementBox';
import { usePrescriptionsApi } from './PrescriptionsPanel.hooks';
import { canContinueAfterPrescriptionsSent } from './PrescriptionsPanel.utils';
import { AnnulledPrescriptionsList } from './sentPrescriptionPackages/AnnulledPrescriptionsList';
import { PrescriptionPackageAccessCodeNotification } from './sentPrescriptionPackages/PrescriptionPackageAccessCodeNotification';
import { PrescriptionPackageView } from './sentPrescriptionPackages/PrescriptionPackageView';
import { PrescriptionPackageViewHeading } from './sentPrescriptionPackages/PrescriptionPackageViewHeading';
import { SentPrescriptionPackagesList } from './sentPrescriptionPackages/SentPrescriptionPackagesList';
import { ClosePrescriptionVisitAction } from './draftPrescriptions/ClosePrescriptionVisitAction';
import { ConfirmPrescriptionVisitAction } from './draftPrescriptions/ConfirmPrescriptionVisitAction';

type PrescriptionsPanelProps = {
   readonly visitId: VisitId;
   readonly patientId: PatientId;
   readonly patientAddress: Address | undefined;
   readonly prescriptionPackages: PrescriptionPackage[];
   readonly onPrescriptionsSent: OnPrescriptionsSent;
   readonly prescriptionsApiRef: Ref<PrescriptionsApi>;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly inPersonExaminationPerformed: boolean;
   readonly documentationScope: Visit['documentationScope'];
};

export const PrescriptionsPanel = (props: PrescriptionsPanelProps): JSX.Element => {
   const scrollRef = useRef<ScrollHandle>(null);
   const { draftPrescriptionsFieldArrayMethodsRef } = usePrescriptionsApi(props.prescriptionsApiRef, scrollRef);
   const draftPrescriptionsListUpdateApiRef = useRef<DraftPrescriptionsListUpdateApi>(null);

   const {
      unsentPrescriptions,
      initialRecipientCategory,
      annulledPrescriptionPackages,
      validatedPrescriptionPackages,
      sentPrescriptionPackages,
      sentPrescriptionsCount,
   } = usePrescriptionPackages(props.prescriptionPackages);

   const createSendPrescriptionsSuccessHandler = (onSuccess: VoidFunction): OnPrescriptionsSent => (
      prescriptionPackages
   ) => {
      draftPrescriptionsListUpdateApiRef.current?.disable();
      props.onPrescriptionsSent(prescriptionPackages);
      if (canContinueAfterPrescriptionsSent(prescriptionPackages)) {
         onSuccess();
      }
   };

   return (
      <Scroll ref={scrollRef} testId={'prescriptions'}>
         <Panel testId={'prescriptions'} direction={'column'} gap={'19px'}>
            <DraftPrescriptionsForm
               visitId={props.visitId}
               patientId={props.patientId}
               patientAddress={props.patientAddress}
               visitDiagnoses={props.visitDiagnoses}
               unsentPrescriptions={unsentPrescriptions}
               initialRecipientCategory={initialRecipientCategory}
               draftPrescriptionsFieldArrayMethodsRef={draftPrescriptionsFieldArrayMethodsRef}
               inPersonExaminationPerformed={props.inPersonExaminationPerformed}
            >
               {({ control, formState, handleSubmitDraft, handleSubmit, trigger, watch }) => (
                  <>
                     <FormValidationSnackbar formState={formState} />
                     <DraftPrescriptionsRouteLeavingGuard
                        visitId={props.visitId}
                        handleSubmitDraft={handleSubmitDraft}
                        formState={formState}
                        sentPrescriptionPackages={sentPrescriptionPackages}
                     />
                     <DraftPrescriptionsListUpdate
                        visitId={props.visitId}
                        handleSubmitDraft={handleSubmitDraft}
                        watch={watch}
                        sentPrescriptionPackages={sentPrescriptionPackages}
                        draftPrescriptionsListUpdateApiRef={draftPrescriptionsListUpdateApiRef}
                     />
                     {documentationScopeVisitor(props.documentationScope)({
                        full: () => (
                           <SendPrescriptionAction
                              documentationScope={props.documentationScope}
                              visitId={props.visitId}
                              visitDiagnoses={props.visitDiagnoses}
                              patientId={props.patientId}
                              sentPrescriptionPackages={sentPrescriptionPackages}
                              onPrescriptionsSent={props.onPrescriptionsSent}
                              handleSubmit={handleSubmit}
                              trigger={trigger}
                              control={control}
                           >
                              {({ sendPrescriptions, disabled }) => (
                                 <Button
                                    label={draftPrescriptionsTranslationKeys.sendPrescriptions}
                                    variant={ButtonVariant.PRIMARY}
                                    disabled={disabled}
                                    onClick={sendPrescriptions}
                                    testId={'sendPrescriptions'}
                                    compact
                                    blockingOperation={draftPrescriptionsTranslationKeys.submittingPrescription}
                                 />
                              )}
                           </SendPrescriptionAction>
                        ),
                        prescriptionOnly: () => (
                           <>
                              <ClosePrescriptionVisitAction visitId={props.visitId}>
                                 {({ closeVisit }) => (
                                    <SendPrescriptionAction
                                       documentationScope={props.documentationScope}
                                       visitId={props.visitId}
                                       visitDiagnoses={props.visitDiagnoses}
                                       patientId={props.patientId}
                                       sentPrescriptionPackages={sentPrescriptionPackages}
                                       onPrescriptionsSent={createSendPrescriptionsSuccessHandler(closeVisit)}
                                       handleSubmit={handleSubmit}
                                       trigger={trigger}
                                       control={control}
                                    >
                                       {({ sendPrescriptions }) => (
                                          <Button
                                             label={draftPrescriptionsTranslationKeys.sendPrescriptionsAndClose}
                                             variant={ButtonVariant.SECONDARY}
                                             onClick={sendPrescriptions}
                                             testId={'sendPrescriptionsAndClose'}
                                             compact
                                             blockingOperation={
                                                draftPrescriptionsTranslationKeys.submittingPrescription
                                             }
                                          />
                                       )}
                                    </SendPrescriptionAction>
                                 )}
                              </ClosePrescriptionVisitAction>
                              <ConfirmPrescriptionVisitAction visitId={props.visitId}>
                                 {({ confirmVisit }) => (
                                    <SendPrescriptionAction
                                       documentationScope={props.documentationScope}
                                       visitId={props.visitId}
                                       visitDiagnoses={props.visitDiagnoses}
                                       patientId={props.patientId}
                                       sentPrescriptionPackages={sentPrescriptionPackages}
                                       onPrescriptionsSent={createSendPrescriptionsSuccessHandler(confirmVisit)}
                                       handleSubmit={handleSubmit}
                                       trigger={trigger}
                                       control={control}
                                    >
                                       {({ sendPrescriptions }) => (
                                          <PrescriptionVisitDataRequiredActionInterceptor
                                             visitDiagnoses={props.visitDiagnoses}
                                          >
                                             <Button
                                                label={
                                                   draftPrescriptionsTranslationKeys.sendPrescriptionsAndConfirmVisit
                                                }
                                                variant={ButtonVariant.PRIMARY}
                                                onClick={sendPrescriptions}
                                                testId={'sendPrescriptionsAndConfirmVisit'}
                                                compact
                                                blockingOperation={
                                                   draftPrescriptionsTranslationKeys.submittingPrescription
                                                }
                                             />
                                          </PrescriptionVisitDataRequiredActionInterceptor>
                                       )}
                                    </SendPrescriptionAction>
                                 )}
                              </ConfirmPrescriptionVisitAction>
                           </>
                        ),
                     })}
                     <PrescriptionsAdvertisementBoxPortal>
                        <PrescriptionsBanner
                           sentPrescriptionsCount={sentPrescriptionsCount}
                           control={control}
                           visitId={props.visitId}
                        />
                     </PrescriptionsAdvertisementBoxPortal>
                  </>
               )}
            </DraftPrescriptionsForm>

            {validatedPrescriptionPackages.length > 0 && (
               <SentPrescriptionPackagesList
                  sentPrescriptionPackages={validatedPrescriptionPackages}
                  visitDiagnoses={props.visitDiagnoses}
                  packageViewRenderer={({ id, accessCode, packageNumber, renderPrescriptions }) => (
                     <PrescriptionPackageView
                        key={id}
                        testId={id.toString()}
                        header={
                           <StyledFlex
                              direction={'row'}
                              justifyContent={'space-between'}
                              alignItems={'flex-end'}
                              gap={'8px'}
                           >
                              <StyledFlex direction={'row'} gap={'12px'}>
                                 <PrescriptionPackageViewHeading packageNumber={packageNumber} />
                                 <PrescriptionPackageAccessCodeNotification accessCode={accessCode} />
                              </StyledFlex>
                              <PrescriptionPackagePrintLink prescriptionPackageId={id} />
                           </StyledFlex>
                        }
                     >
                        {renderPrescriptions()}
                     </PrescriptionPackageView>
                  )}
                  visitId={props.visitId}
               />
            )}

            <PrescriptionsAdvertisementBox />

            {isEmpty(annulledPrescriptionPackages) ? null : (
               <AnnulledPrescriptionsList
                  visitDiagnoses={props.visitDiagnoses}
                  annulledPrescriptionPackages={annulledPrescriptionPackages}
               />
            )}
         </Panel>
      </Scroll>
   );
};
